import { useState, type FormEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isIsoDate, MAX_DURATION_MINUTES, MAX_PREP_DAYS, taskInputSchema, type TaskDto } from "@habitos/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const COLORS = ["#0ea5e9", "#6366f1", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#8b5cf6", "#64748b"];

export function TaskEditor() {
  const { id } = useParams();
  const task = useQuery({ queryKey: ["task", id], queryFn: () => api.task(id!), enabled: !!id });

  if (id && task.isPending) return <p className="muted center">Cargando…</p>;
  if (id && !task.data) return <p className="muted center">Esta tarea no existe.</p>;
  return <TaskForm key={task.data?.id ?? "new"} existing={task.data} />;
}

function TaskForm({ existing }: { existing: TaskDto | undefined }) {
  const { today } = useAuth();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [params] = useSearchParams();
  const requested = params.get("date");
  const initialDate = existing?.date ?? (requested && isIsoDate(requested) ? requested : today);

  const [hasTime, setHasTime] = useState(existing ? existing.time !== null : true);
  const [focus, setFocus] = useState(existing?.focus ?? false);
  const [prep, setPrep] = useState(existing ? existing.prepDaysBefore !== null : false);
  const [color, setColor] = useState(existing?.color ?? COLORS[0]!);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = async (date: string) => {
    await client.invalidateQueries({ queryKey: ["tasks"] });
    await client.invalidateQueries({ queryKey: ["task"] });
    navigate(date === today ? "/" : `/?date=${date}`);
  };

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const parsed = taskInputSchema.safeParse({
      title: form.get("title"),
      emoji: String(form.get("emoji") ?? "").trim() || null,
      color,
      date: form.get("date"),
      time: hasTime ? form.get("time") : null,
      durationMinutes: Number(form.get("durationMinutes")),
      notes: String(form.get("notes") ?? "").trim() || null,
      focus,
      focusMinutes: focus ? Number(form.get("focusMinutes")) : (existing?.focusMinutes ?? 25),
      breakMinutes: focus ? Number(form.get("breakMinutes")) : (existing?.breakMinutes ?? 5),
      reminder: hasTime && form.get("reminder") === "on",
      prepDaysBefore: prep ? Number(form.get("prepDaysBefore")) : null,
      prepNote: prep ? String(form.get("prepNote") ?? "").trim() || null : null,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Revisa los datos");
      return;
    }
    setBusy(true);
    try {
      if (existing) await api.updateTask(existing.id, parsed.data);
      else await api.createTask(parsed.data);
      await finish(parsed.data.date);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  async function remove() {
    if (!existing || !confirm("¿Borrar esta tarea?")) return;
    await api.deleteTask(existing.id);
    await finish(existing.date);
  }

  return (
    <>
      <header className="page-header row start">
        <button className="icon-btn" aria-label="Volver" onClick={() => navigate(-1)}>‹</button>
        <h1>{existing ? "Editar tarea" : "Nueva tarea"}</h1>
      </header>

      <form onSubmit={onSubmit} className="card form">
        <div className="row gap">
          <label className="emoji-field">
            Ícono
            <input name="emoji" defaultValue={existing?.emoji ?? ""} placeholder="🧺" maxLength={8} />
          </label>
          <label className="grow">
            ¿Qué tienes que hacer?
            <input name="title" required maxLength={120} defaultValue={existing?.title} placeholder="Doblar la ropa" />
          </label>
        </div>

        <label>
          Día
          <input name="date" type="date" required defaultValue={initialDate} />
        </label>

        <label className="toggle">
          <input type="checkbox" checked={hasTime} onChange={(e) => setHasTime(e.target.checked)} />
          A una hora concreta
        </label>

        <div className="row gap">
          {hasTime && (
            <label className="grow">
              Hora
              <input name="time" type="time" required defaultValue={existing?.time ?? "09:00"} />
            </label>
          )}
          <label className="grow">
            Duración (min)
            <input
              name="durationMinutes"
              type="number"
              min={5}
              max={MAX_DURATION_MINUTES}
              step={5}
              defaultValue={existing?.durationMinutes ?? 30}
            />
          </label>
        </div>

        <label className="toggle">
          <input type="checkbox" checked={focus} onChange={(e) => setFocus(e.target.checked)} />
          Hacerla en bloques con pausas (Pomodoro)
        </label>
        {focus && (
          <div className="row gap">
            <label className="grow">
              Trabajo (min)
              <input name="focusMinutes" type="number" min={5} max={MAX_DURATION_MINUTES} step={5} defaultValue={existing?.focusMinutes ?? 25} />
            </label>
            <label className="grow">
              Pausa (min)
              <input name="breakMinutes" type="number" min={1} max={60} defaultValue={existing?.breakMinutes ?? 5} />
            </label>
          </div>
        )}

        <label className="toggle">
          <input type="checkbox" checked={prep} onChange={(e) => setPrep(e.target.checked)} />
          Avisarme antes para preparar algo
        </label>
        {prep && (
          <div className="row gap">
            <label className="grow">
              ¿Qué hay que preparar?
              <input name="prepNote" maxLength={200} defaultValue={existing?.prepNote ?? ""} placeholder="Comprar el regalo" />
            </label>
            <label className="days-field">
              Días antes
              <input
                name="prepDaysBefore"
                type="number"
                min={1}
                max={MAX_PREP_DAYS}
                defaultValue={existing?.prepDaysBefore ?? 1}
              />
            </label>
          </div>
        )}

        <label>
          <span>Notas <span className="muted">(opcional)</span></span>
          <textarea
            name="notes"
            className="textarea"
            maxLength={1000}
            defaultValue={existing?.notes ?? ""}
            placeholder="Qué llevar, a quién llamar…"
          />
        </label>

        <fieldset>
          <legend>Color</legend>
          <div className="chips">
            {COLORS.map((c) => (
              <button
                type="button"
                key={c}
                className={`swatch ${c === color ? "on" : ""}`}
                style={{ background: c }}
                aria-label={c}
                aria-pressed={c === color}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </fieldset>

        {hasTime && (
          <label className="toggle">
            <input name="reminder" type="checkbox" defaultChecked={existing?.reminder ?? true} />
            Recordarme a esa hora
          </label>
        )}

        {error && <p className="error">{error}</p>}
        <button className="btn primary" disabled={busy}>{existing ? "Guardar" : "Agregar tarea"}</button>
      </form>

      {existing && (
        <div className="row gap danger-zone">
          <button className="btn danger" onClick={remove}>Borrar</button>
        </div>
      )}
    </>
  );
}
